'use client';
import { useState, useEffect, useId } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FAQItem {
  question: string;
  answer: string;
}

interface FormData {
  naam: string;
  bedrijfsnaam: string;
  beroep: string;
  email: string;
  telefoonnummer: string;
  stad: string;
  omschrijving: string;
  contactDatum: string;
  contactDagdeel: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const targetGroups = [
  {
    icon: '🔧',
    title: 'Loodgieters',
    text: 'Monteer frenaCal als waardevolle upgrade bij elke leidinginstallatie of renovatie.',
  },
  {
    icon: '🍳',
    title: 'Keukenspeciaalzaken',
    text: 'Bied klanten een complete keukenoplossing — inclusief kalkvrij water voor hun apparatuur.',
  },
  {
    icon: '🏗️',
    title: 'Installatiebedrijven',
    text: 'Voeg een onderhoudsvriendelijk product toe aan je portfolio zonder servicedruk.',
  },
  {
    icon: '🔥',
    title: 'CV-onderhoudsmonteurs',
    text: 'Verleng de levensduur van CV-ketels bij je klanten en verhoog de factuurwaarde per bezoek.',
  },
];

const usps = [
  {
    title: 'Marge zonder moeite',
    body: 'Vaste dealer-marge op elk product. Geen voorraadbeheer vereist. Direct levering aan jouw klant of naar jouw adres.',
  },
  {
    title: 'Eén installatie, nul terugkomverplichtingen',
    body: 'frenaCal vraagt geen periodiek onderhoud. Dat betekent geen servicecontract voor jou, maar ook geen klachten na montage. Eenmalig installeren, blijvend tevreden klant.',
  },
  {
    title: 'Wetenschappelijk bewezen product',
    body: 'Klanten vragen bewijs. Dat hebben wij: validaties van University of Reading, Texas A&M en GSA. Jij verkoopt met vertrouwen.',
  },
];

const steps = [
  {
    num: '01',
    title: 'Aanmelden',
    body: 'Vul het partnerformulier in. Wij nemen binnen 2 werkdagen contact op.',
  },
  {
    num: '02',
    title: 'Onboarding & documentatie',
    body: 'Je ontvangt toegang tot het partnerportaal met technische specs, installatiehandleidingen, WRAS-goedkeuring, verkoopmateriaal en je persoonlijke margestructuur.',
  },
  {
    num: '03',
    title: 'Eerste installatie',
    body: 'Montage duurt gemiddeld 1 uur. Geen speciaal gereedschap, geen elektra, geen zoutaansluiting. Wij trainen je desgewenst on-site of via video.',
  },
  {
    num: '04',
    title: 'Verdienen & opschalen',
    body: 'Factureer jouw klant. Wij leveren. Jij houdt de marge. Hoe meer installaties, hoe beter de condities.',
  },
];

const bullets = [
  'Catalytische waterbehandeling — geen zout, geen stroom, geen chemicaliën',
  'Installatietijd: gemiddeld 60 minuten',
  'Geschikt voor alle leidingdiameters (15mm–54mm)',
  'WRAS-gecertificeerd — geschikt voor drinkwaterinstallaties',
  'Levensduur: 15+ jaar zonder vervanging',
  'Wetenschappelijk gevalideerd (University of Reading, Texas A&M, GSA)',
  'Geen filter te vervangen, geen servicebeurt nodig',
];

const testimonials = [
  {
    quote:
      'Ik monteer nu frenaCal bij elke nieuwe cv-installatie als standaard advies. Klanten zijn blij, en ik verdien er goed aan.',
    name: 'Marco V.',
    company: 'Installatiebedrijf',
    city: 'Utrecht',
  },
  {
    quote:
      'Het product vraagt niks van me na montage. Geen terugkomverplichting, geen klachten. Precies wat ik zoek.',
    name: 'Pieter D.',
    company: 'Loodgieter',
    city: 'Amsterdam',
  },
  {
    quote:
      'Onze keukenklanten zijn blij met kalkvrij water bij hun apparatuur. frenaCal is een logische aanvulling op ons assortiment.',
    name: 'Sandra K.',
    company: 'Keukenspeciaalzaak',
    city: 'Eindhoven',
  },
];

const faqs: FAQItem[] = [
  {
    question: 'Moet ik een minimale afname doen om partner te worden?',
    answer:
      'Nee. Er is geen minimale ordergrootte of verplichte voorraad. Je bestelt per project.',
  },
  {
    question: 'Hoe zit het met de garantie richting mijn klant?',
    answer:
      'frenaCal biedt een fabrieksgarantie van 10 jaar. Als partner kun je dit als extra verkoopargument inzetten.',
  },
  {
    question: 'Hoe snel kan ik na aanmelding aan de slag?',
    answer:
      'Na goedkeuring ontvang je binnen 3 werkdagen toegang tot het partnerportaal en alle benodigde documentatie.',
  },
  {
    question: 'Is er technische ondersteuning beschikbaar?',
    answer:
      'Ja. Ons team staat klaar voor technische vragen via telefoon en e-mail. Ook bieden we installatie-instructievideo\'s en een gedetailleerde handleiding.',
  },
  {
    question: 'Verschilt frenaCal van magneetontharders of zoutontharders?',
    answer:
      'Ja, significant. frenaCal werkt op katalytische basis — wetenschappelijk gevalideerd door drie universiteiten. Geen zout, geen stroom, geen regelmatig onderhoud.',
  },
  {
    question: 'Kan ik frenaCal combineren met mijn eigen huisstijl?',
    answer:
      'Ja, beperkte co-brandingmogelijkheden zijn bespreekbaar voor grotere partners.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StarRating() {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} className="w-4 h-4 text-[#33BEBE] fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = useId();

  return (
    <div className="divide-y divide-gray-200">
      {items.map((item, i) => (
        <div key={i}>
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            aria-expanded={openIndex === i}
            aria-controls={`${baseId}-answer-${i}`}
            id={`${baseId}-question-${i}`}
            className="w-full flex items-center justify-between py-5 text-left group"
          >
            <span className="font-heading font-semibold text-[#1B3A6B] text-base md:text-lg pr-4 group-hover:text-[#33BEBE] transition-colors">
              {item.question}
            </span>
            <span
              className={`flex-shrink-0 w-7 h-7 rounded-full border-2 border-[#33BEBE] flex items-center justify-center transition-transform duration-300 ${
                openIndex === i ? 'rotate-45' : ''
              }`}
            >
              <svg className="w-3 h-3 text-[#33BEBE]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </span>
          </button>
          <div
            id={`${baseId}-answer-${i}`}
            role="region"
            aria-labelledby={`${baseId}-question-${i}`}
            className={`overflow-hidden transition-all duration-300 ${
              openIndex === i ? 'max-h-96 pb-5' : 'max-h-0'
            }`}
          >
            <p className="font-sans text-gray-600 leading-relaxed">{item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PartnersPage() {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    naam: '',
    bedrijfsnaam: '',
    beroep: '',
    email: '',
    telefoonnummer: '',
    stad: '',
    omschrijving: '',
    contactDatum: '',
    contactDagdeel: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.08 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/partner-aanvraag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Er is iets misgegaan');
      }

      // GA4 event via dataLayer
      if (typeof window !== 'undefined' && (window as unknown as { dataLayer?: unknown[] }).dataLayer) {
        (window as unknown as { dataLayer: unknown[] }).dataLayer.push({
          event: 'Partner Lead Submitted',
          partner_sector: formData.beroep,
          partner_city: formData.stad,
        });
      }

      router.push('/partners/bedankt');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Er is iets misgegaan. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen">

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <section className="bg-[#1B3A6B] pt-28 pb-20 md:pt-36 md:pb-28 relative overflow-hidden">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#33BEBE] blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#33BEBE] blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#33BEBE]/20 border border-[#33BEBE]/40 rounded-full px-4 py-1.5 mb-6">
                  <span className="w-2 h-2 rounded-full bg-[#33BEBE] animate-pulse" />
                  <span className="text-[#33BEBE] text-sm font-sans font-semibold tracking-wide">Partnerprogramma</span>
                </div>

                <h1 className="font-heading font-semibold text-white text-4xl md:text-5xl leading-tight mb-5">
                  Word partner van frenaCal.{' '}
                  <span className="text-[#33BEBE]">Verdien meer op elke installatie.</span>
                </h1>

                <p className="font-sans text-gray-300 text-lg md:text-xl leading-relaxed mb-8 max-w-lg">
                  Een product dat zichzelf verkoopt. Geen onderhoud voor jouw klant. Vaste marge voor jou.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="#aanmelden"
                    className="inline-flex items-center justify-center rounded-xl bg-[#1B3A6B] border-2 border-[#33BEBE] text-white font-heading font-semibold px-7 py-3.5 hover:bg-[#33BEBE] hover:text-[#1B3A6B] transition-all duration-200"
                  >
                    Neem contact met mij op
                  </a>
                  <a
                    href="#product"
                    className="inline-flex items-center justify-center rounded-xl border-2 border-[#33BEBE]/60 text-[#33BEBE] font-heading font-semibold px-7 py-3.5 hover:border-[#33BEBE] hover:bg-[#33BEBE]/10 transition-all duration-200"
                  >
                    Meer informatie
                  </a>
                </div>
              </div>

              {/* Product visual */}
              <div className="flex justify-center md:justify-end">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#33BEBE]/20 rounded-3xl blur-2xl scale-110" />
                  <div className="relative bg-[#2F4F6E]/60 border border-[#33BEBE]/30 rounded-3xl p-8 backdrop-blur-sm">
                    <Image
                      src="/assets/images/Frenacal_Installateur-1789576466004.png"
                      alt="FrenaCal installateur staat aan de deur klaar voor installatie"
                      width={400}
                      height={300}
                      className="rounded-2xl object-cover w-full max-w-sm"
                    />
                    <div className="mt-4 flex items-center gap-3">
                      <div className="flex-1 bg-[#1B3A6B]/80 rounded-xl p-3 border border-[#33BEBE]/20">
                        <p className="text-[#33BEBE] text-xs font-sans font-semibold uppercase tracking-wider">Installatietijd</p>
                        <p className="text-white font-heading font-semibold text-lg">~60 min</p>
                      </div>
                      <div className="flex-1 bg-[#1B3A6B]/80 rounded-xl p-3 border border-[#33BEBE]/20">
                        <p className="text-[#33BEBE] text-xs font-sans font-semibold uppercase tracking-wider">Garantie</p>
                        <p className="text-white font-heading font-semibold text-lg">10 jaar</p>
                      </div>
                      <div className="flex-1 bg-[#1B3A6B]/80 rounded-xl p-3 border border-[#33BEBE]/20">
                        <p className="text-[#33BEBE] text-xs font-sans font-semibold uppercase tracking-wider">Levensduur</p>
                        <p className="text-white font-heading font-semibold text-lg">15+ jaar</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTIE 1 — Doelgroepherkenning ───────────────────────────────── */}
        <section className="bg-gradient-to-br from-[#e8f4fd] via-[#dff0fb] to-[#c8e8f7] py-20 md:py-24 reveal">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-semibold text-[#1B3A6B] text-3xl md:text-4xl mb-3">
                Voor wie is het partnerprogramma?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {targetGroups.map((group, i) => (
                <div
                  key={i}
                  className="relative group"
                  style={{
                    perspective: '1000px',
                  }}
                >
                  {/* 3D card wrapper */}
                  <div
                    className="relative rounded-2xl p-7 transition-all duration-500 ease-out cursor-default"
                    style={{
                      background: 'rgba(255, 255, 255, 0.35)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid rgba(51, 190, 190, 0.35)',
                      boxShadow:
                        '0 8px 32px rgba(27, 58, 107, 0.12), 0 2px 8px rgba(51, 190, 190, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
                      transform: 'translateZ(0) rotateX(0deg)',
                    }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateY(-8px) rotateX(4deg) rotateY(-2deg) scale(1.03)';
                      el.style.boxShadow =
                        '0 24px 48px rgba(27, 58, 107, 0.22), 0 8px 20px rgba(51, 190, 190, 0.25), inset 0 1px 0 rgba(255,255,255,0.7)';
                      el.style.border = '1px solid rgba(51, 190, 190, 0.65)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateZ(0) rotateX(0deg)';
                      el.style.boxShadow =
                        '0 8px 32px rgba(27, 58, 107, 0.12), 0 2px 8px rgba(51, 190, 190, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)';
                      el.style.border = '1px solid rgba(51, 190, 190, 0.35)';
                    }}
                  >
                    {/* Glossy top highlight */}
                    <div
                      className="absolute top-0 left-0 right-0 h-1/2 rounded-t-2xl pointer-events-none"
                      style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)',
                      }}
                    />
                    {/* Icon bubble */}
                    <div
                      className="relative z-10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 text-3xl"
                      style={{
                        background: 'linear-gradient(135deg, rgba(51,190,190,0.25) 0%, rgba(27,58,107,0.15) 100%)',
                        boxShadow: '0 4px 12px rgba(51,190,190,0.2), inset 0 1px 0 rgba(255,255,255,0.5)',
                        border: '1px solid rgba(51,190,190,0.3)',
                      }}
                    >
                      {group.icon}
                    </div>
                    <h3 className="relative z-10 font-heading font-semibold text-[#1B3A6B] text-lg mb-2">
                      {group.title}
                    </h3>
                    <p className="relative z-10 font-sans text-[#2F4F6E] text-sm leading-relaxed opacity-85">{group.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTIE 2 — USP blokken ───────────────────────────────────────── */}
        <section className="bg-white py-20 md:py-24 reveal">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-semibold text-[#1B3A6B] text-3xl md:text-4xl mb-3">
                Waarom frenaCal in jouw portfolio?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {usps.map((usp, i) => (
                <div key={i} className="relative">
                  <div className="absolute top-0 left-0 w-12 h-12 rounded-xl bg-[#33BEBE]/15 flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#33BEBE]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      {i === 0 && <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                      {i === 1 && <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />}
                      {i === 2 && <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
                    </svg>
                  </div>
                  <div className="pt-16">
                    <h3 className="font-heading font-semibold text-[#1B3A6B] text-xl mb-3">{usp.title}</h3>
                    <p className="font-sans text-gray-600 leading-relaxed">{usp.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTIE 3 — Hoe werkt het ─────────────────────────────────────── */}
        <section className="bg-[#1B3A6B] py-20 md:py-24 reveal">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading font-semibold text-white text-3xl md:text-4xl mb-3">
                Zo simpel werkt het
              </h2>
            </div>

            {/* Desktop: horizontal timeline | Mobile: vertical */}
            <div className="hidden md:grid grid-cols-4 gap-6 relative">
              {/* Connector line */}
              <div className="absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-[#33BEBE]/30" />

              {steps.map((step, i) => (
                <div key={i} className="relative flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full border-2 border-[#33BEBE] bg-[#1B3A6B] flex items-center justify-center mb-5 relative z-10">
                    <span className="font-heading font-semibold text-[#33BEBE] text-lg">{step.num}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-white text-base mb-2">{step.title}</h3>
                  <p className="font-sans text-gray-300 text-sm leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>

            {/* Mobile vertical */}
            <div className="md:hidden space-y-8">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-[#33BEBE] flex items-center justify-center">
                    <span className="font-heading font-semibold text-[#33BEBE] text-sm">{step.num}</span>
                  </div>
                  <div className="pt-2">
                    <h3 className="font-heading font-semibold text-white text-base mb-1">{step.title}</h3>
                    <p className="font-sans text-gray-300 text-sm leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTIE 4 — Product specs ─────────────────────────────────────── */}
        <section id="product" className="bg-white py-20 md:py-24 reveal">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-semibold text-[#1B3A6B] text-3xl md:text-4xl mb-3">
                Wat jij installeert
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <ul className="space-y-4">
                  {bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-[#33BEBE] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-sans text-gray-700 leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 font-sans italic text-[#2F4F6E] text-sm leading-relaxed border-l-2 border-[#33BEBE] pl-4">
                  "Klanten die frenaCal hebben, bellen niet terug voor onderhoud. Dat scheelt jou tijd en hen geld."
                </p>
              </div>

              <div className="order-1 md:order-2 flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#33BEBE]/10 rounded-3xl blur-xl scale-105" />
                  <Image
                    src="/assets/images/Untitled-1772981249722.jpeg"
                    alt="frenaCal apparaat technische weergave — katalytische waterbehandeling unit"
                    width={480}
                    height={360}
                    className="relative rounded-2xl object-cover shadow-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTIE 5 — Testimonials ──────────────────────────────────────── */}
        <section className="bg-gradient-to-br from-[#e8f4fd] via-[#dff0fb] to-[#c8e8f7] py-20 md:py-24 reveal">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-semibold text-[#1B3A6B] text-3xl md:text-4xl mb-3">
                Wat andere partners zeggen
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="relative group"
                  style={{ perspective: '1000px' }}
                >
                  <div
                    className="relative rounded-2xl p-7 transition-all duration-500 ease-out cursor-default"
                    style={{
                      background: 'rgba(255, 255, 255, 0.35)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid rgba(51, 190, 190, 0.35)',
                      boxShadow:
                        '0 8px 32px rgba(27, 58, 107, 0.12), 0 2px 8px rgba(51, 190, 190, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
                      transform: 'translateZ(0) rotateX(0deg)',
                    }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateY(-8px) rotateX(4deg) rotateY(-2deg) scale(1.03)';
                      el.style.boxShadow =
                        '0 24px 48px rgba(27, 58, 107, 0.22), 0 8px 20px rgba(51, 190, 190, 0.25), inset 0 1px 0 rgba(255,255,255,0.7)';
                      el.style.border = '1px solid rgba(51, 190, 190, 0.65)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateZ(0) rotateX(0deg)';
                      el.style.boxShadow =
                        '0 8px 32px rgba(27, 58, 107, 0.12), 0 2px 8px rgba(51, 190, 190, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)';
                      el.style.border = '1px solid rgba(51, 190, 190, 0.35)';
                    }}
                  >
                    {/* Glossy top highlight */}
                    <div
                      className="absolute top-0 left-0 right-0 h-1/2 rounded-t-2xl pointer-events-none"
                      style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)',
                      }}
                    />
                    {/* Star rating with icon bubble style */}
                    <div
                      className="relative z-10 inline-flex items-center px-3 py-1.5 rounded-xl mb-4"
                      style={{
                        background: 'linear-gradient(135deg, rgba(51,190,190,0.25) 0%, rgba(27,58,107,0.15) 100%)',
                        boxShadow: '0 4px 12px rgba(51,190,190,0.2), inset 0 1px 0 rgba(255,255,255,0.5)',
                        border: '1px solid rgba(51,190,190,0.3)',
                      }}
                    >
                      <StarRating />
                    </div>
                    <p className="relative z-10 font-sans italic text-[#2F4F6E] text-base leading-relaxed mb-5 opacity-90">
                      "{t.quote}"
                    </p>
                    <div
                      className="relative z-10 border-t pt-4"
                      style={{ borderColor: 'rgba(51, 190, 190, 0.25)' }}
                    >
                      <p className="font-heading font-semibold text-[#1B3A6B] text-sm">{t.name}</p>
                      <p className="font-sans text-[#2F4F6E] text-xs mt-0.5 opacity-75">
                        {t.company} · {t.city}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTIE 6 — FAQ ───────────────────────────────────────────────── */}
        <section className="bg-white py-20 md:py-24 reveal">
          <div className="mx-auto max-w-3xl px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-semibold text-[#1B3A6B] text-3xl md:text-4xl mb-3">
                Veelgestelde vragen van partners
              </h2>
            </div>
            <FAQAccordion items={faqs} />
          </div>
        </section>

        {/* ── SECTIE 7 — Aanmeldformulier ──────────────────────────────────── */}
        <section id="aanmelden" className="bg-[#1B3A6B] py-20 md:py-24 reveal">
          <div className="mx-auto max-w-2xl px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-semibold text-white text-3xl md:text-4xl mb-4">
                Klaar om partner te worden?
              </h2>
              <p className="font-sans text-gray-300 text-lg leading-relaxed">
                Vul het formulier in. Wij nemen binnen 2 werkdagen contact met je op voor een persoonlijk gesprek over de mogelijkheden.
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#33BEBE]/20 border border-[#33BEBE]/50 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#33BEBE]/30 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#33BEBE]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-heading font-semibold text-white text-2xl mb-2">Aanmelding ontvangen!</h3>
                <p className="font-sans text-gray-300">
                  Bedankt voor je interesse. We nemen binnen 2 werkdagen contact met je op.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="naam">
                      Naam <span className="text-[#33BEBE]">*</span>
                    </label>
                    <input
                      id="naam"
                      name="naam"
                      type="text"
                      required
                      value={formData.naam}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors"
                      placeholder="Jouw naam"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="bedrijfsnaam">
                      Bedrijfsnaam <span className="text-[#33BEBE]">*</span>
                    </label>
                    <input
                      id="bedrijfsnaam"
                      name="bedrijfsnaam"
                      type="text"
                      required
                      value={formData.bedrijfsnaam}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors"
                      placeholder="Naam van je bedrijf"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="beroep">
                    Beroep / sector
                  </label>
                  <select
                    id="beroep"
                    name="beroep"
                    value={formData.beroep}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans focus:outline-none focus:border-[#33BEBE] transition-colors appearance-none"
                  >
                    <option value="" className="bg-[#1B3A6B]">Selecteer jouw sector</option>
                    <option value="Loodgieter" className="bg-[#1B3A6B]">Loodgieter</option>
                    <option value="Installatiebedrijf" className="bg-[#1B3A6B]">Installatiebedrijf</option>
                    <option value="Keukenspeciaalzaak" className="bg-[#1B3A6B]">Keukenspeciaalzaak</option>
                    <option value="CV-monteur" className="bg-[#1B3A6B]">CV-monteur</option>
                    <option value="Anders" className="bg-[#1B3A6B]">Anders</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="email">
                      E-mailadres <span className="text-[#33BEBE]">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors"
                      placeholder="jouw@email.nl"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="telefoonnummer">
                      Telefoonnummer
                    </label>
                    <input
                      id="telefoonnummer"
                      name="telefoonnummer"
                      type="tel"
                      value={formData.telefoonnummer}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors"
                      placeholder="+31 6 12345678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="stad">
                    Stad / regio
                  </label>
                  <input
                    id="stad"
                    name="stad"
                    type="text"
                    value={formData.stad}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors"
                    placeholder="Amsterdam"
                  />
                </div>

                <div>
                  <label className="block font-sans text-gray-300 text-sm mb-1.5" htmlFor="omschrijving">
                    Korte omschrijving activiteiten
                  </label>
                  <textarea
                    id="omschrijving"
                    name="omschrijving"
                    rows={3}
                    value={formData.omschrijving}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors resize-none"
                    placeholder="Vertel kort wat je doet en hoeveel installaties je gemiddeld per maand uitvoert..."
                  />
                </div>

                <div>
                  <label className="block font-sans text-gray-300 text-sm mb-2">
                    Gewenst contactmoment <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-sans text-gray-400 text-xs mb-1.5" htmlFor="contactDatum">
                        Voorkeursdatum
                      </label>
                      <input
                        id="contactDatum"
                        name="contactDatum"
                        type="date"
                        value={formData.contactDatum}
                        onChange={handleChange}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans placeholder-gray-400 focus:outline-none focus:border-[#33BEBE] transition-colors [color-scheme:dark]"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-gray-400 text-xs mb-1.5" htmlFor="contactDagdeel">
                        Voorkeursdagdeel
                      </label>
                      <select
                        id="contactDagdeel"
                        name="contactDagdeel"
                        value={formData.contactDagdeel}
                        onChange={handleChange}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-sans focus:outline-none focus:border-[#33BEBE] transition-colors appearance-none"
                      >
                        <option value="" className="bg-[#1B3A6B]">Geen voorkeur</option>
                        <option value="Ochtend (9:00–12:00)" className="bg-[#1B3A6B]">Ochtend (9:00–12:00)</option>
                        <option value="Middag (12:00–17:00)" className="bg-[#1B3A6B]">Middag (12:00–17:00)</option>
                        <option value="Avond (17:00–20:00)" className="bg-[#1B3A6B]">Avond (17:00–20:00)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/40 rounded-xl px-4 py-3">
                    <p className="font-sans text-red-300 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#33BEBE] text-[#1B3A6B] font-heading font-bold rounded-xl py-4 text-base hover:bg-[#2aa8a8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Aanmelding versturen...' : 'Neem contact met mij op'}
                </button>

                <p className="font-sans text-gray-400 text-xs text-center leading-relaxed">
                  Jouw gegevens worden vertrouwelijk behandeld en niet gedeeld met derden.
                </p>
              </form>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
